import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Icon } from 'components/Icons'
import CompletionPie from './CompletionPie'
import {
  SERVICES,
  SERVICE_CATEGORIES,
  ROLE_LABELS,
  getCompletionColor,
  getOverallCompletion,
} from './servicesCatalog'

import styles from './ServicesPage.module.scss'

function ServicesPage() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [activeCat, setActiveCat] = useState('all')
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return SERVICES.filter((s) => {
      if (activeCat !== 'all' && s.category !== activeCat) return false
      if (!q) return true
      return (
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.id.includes(q) ||
        (s.path || '').toLowerCase().includes(q)
      )
    })
  }, [activeCat, query])

  const overall = useMemo(() => getOverallCompletion(), [])

  const stats = useMemo(() => {
    const total = SERVICES.length
    const ready = SERVICES.filter((s) => s.completion >= 80).length
    const wip = SERVICES.filter((s) => s.completion >= 30 && s.completion < 80).length
    const todo = SERVICES.filter((s) => s.completion < 30).length
    return { total, ready, wip, todo }
  }, [])

  const handleOpen = (svc) => {
    if (svc.path) navigate(svc.path)
  }

  return (
    <div className={styles.page}>
      <div className={styles.headline}>
        <div>
          <h1>{t('services.title', 'Сервисы платформы')}</h1>
          <p>
            {t(
              'services.subtitle',
              'Каталог всех сервисов ИС «Чуйский Университет» с показателем готовности и ролями, имеющими доступ. Кликните на карточку, чтобы перейти к сервису.',
            )}
          </p>
        </div>

        <div className={styles.kpis}>
          <div className={styles.kpi}>
            <CompletionPie pct={overall} size={48} stroke={5} fontSize={11} color={getCompletionColor(overall)} />
            <div>
              <div className={styles.kpi__num}>{overall}%</div>
              <div className={styles.kpi__label}>{t('services.kpi_overall', 'Общая готовность')}</div>
            </div>
          </div>
          <div className={styles.kpi}>
            <Icon name="package" size={28} color="var(--color-accent)" />
            <div>
              <div className={styles.kpi__num}>{stats.total}</div>
              <div className={styles.kpi__label}>{t('services.kpi_total', 'Всего сервисов')}</div>
            </div>
          </div>
          <div className={styles.kpi}>
            <Icon name="check-square" size={28} color="#10b981" />
            <div>
              <div className={styles.kpi__num}>{stats.ready}</div>
              <div className={styles.kpi__label}>{t('services.kpi_ready', 'Готово ≥80%')}</div>
            </div>
          </div>
          <div className={styles.kpi}>
            <Icon name="activity" size={28} color="#f59e0b" />
            <div>
              <div className={styles.kpi__num}>{stats.wip}</div>
              <div className={styles.kpi__label}>{t('services.kpi_wip', 'В работе')}</div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.toolbar}>
        <button
          type="button"
          className={`${styles.chip} ${activeCat === 'all' ? styles.active : ''}`}
          onClick={() => setActiveCat('all')}
        >
          {t('services.cat_all', 'Все')} ({SERVICES.length})
        </button>
        {Object.values(SERVICE_CATEGORIES).map((cat) => {
          const count = SERVICES.filter((s) => s.category === cat.key).length
          return (
            <button
              type="button"
              key={cat.key}
              className={`${styles.chip} ${activeCat === cat.key ? styles.active : ''}`}
              onClick={() => setActiveCat(cat.key)}
            >
              <span className={styles.dot} style={{ background: cat.color }} aria-hidden />
              {t(`services.cat_${cat.key}`, cat.label)} ({count})
            </button>
          )
        })}
        <input
          type="search"
          className={styles.search}
          placeholder={t('services.search_placeholder', 'Поиск по сервисам…')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <div className={styles.empty}>{t('services.empty', 'Ничего не найдено')}</div>
      ) : (
        <div className={styles.grid}>
          {filtered.map((svc) => {
            const cat = SERVICE_CATEGORIES[Object.keys(SERVICE_CATEGORIES).find(
              (k) => SERVICE_CATEGORIES[k].key === svc.category,
            )]
            return (
              <div
                key={svc.id}
                className={styles.card}
                role="button"
                tabIndex={0}
                onClick={() => handleOpen(svc)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleOpen(svc)
                  }
                }}
                aria-label={`${svc.title} — ${svc.completion}% готово`}
              >
                <div className={styles.card__pie}>
                  <CompletionPie
                    pct={svc.completion}
                    size={56}
                    stroke={6}
                    fontSize={13}
                    color={getCompletionColor(svc.completion)}
                  />
                </div>

                <div className={styles.card__head}>
                  <span
                    className={styles.card__icon}
                    style={{
                      background: `${cat?.color || 'var(--color-accent)'}1f`,
                      color: cat?.color || 'var(--color-accent)',
                    }}
                  >
                    <Icon name={svc.icon || 'package'} size={20} color="currentColor" />
                  </span>
                  <div>
                    <h3 className={styles.card__title}>{svc.title}</h3>
                    <span className={styles.card__category}>{cat?.label || ''}</span>
                  </div>
                </div>

                <p className={styles.card__desc}>{svc.description}</p>

                {svc.note ? <div className={styles.card__note}>{svc.note}</div> : null}

                <div className={styles.card__rolesWrap}>
                  <div className={styles.card__rolesHead}>
                    <span>{t('services.roles_aria', 'Доступ для ролей')}</span>
                    <span className={styles.card__rolesCount}>{svc.roles.length}</span>
                  </div>
                  <div
                    className={styles.card__roles}
                    aria-label={t('services.roles_aria', 'Доступ для ролей')}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="list"
                  >
                    {svc.roles.map((r) => (
                      <span key={r} className={styles.role} role="listitem">
                        {ROLE_LABELS[r] || r}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ServicesPage
